// Libraries
import React, { useMemo, useRef } from 'react';
import isEqual from 'lodash.isequal';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Types
import { BreadCrumbsProps } from './types';

// Components
import { Typography } from '../../display/Typography';

// Components Styled
import { Container, Item, Link, RightButton } from './styles';
import { PendingCrumb } from './PendingCrumb';

// State
import { getRoidOTSelectedGV } from '../../../state/selectors/ui/modal';
import { indexTabFormReset } from '../../../state/actions/ui/tabs';
import { useDispatch } from 'react-redux';
import { tabsType } from '../../../state/reducers/ui/tabs';
// TODO: Consider using roidOTSelected for future compatibility

// Router
import { RouteUIElement, getRouteLabel } from '../../../router/labelToRoutes';

/** La pestaña Actuaciones de Órdenes de trabajo (adonde vuelve si se entró por ella). */
const ACTIONS_TAB_PATH = '/work-orders/actions';
import { OrderType } from '../../../types/orders';

// Utils
import { formatRoidWithDots } from '../../display/tables/utils/roidFormatter';
import { decompressSessionStorage } from '../../../utils/sessionStorage';
import { buildRoidFallback } from '../../../utils/surveillanceRoute';
import {
  adminTenantIdFromPath,
  initialCrumbRoidState,
  nextCrumbRoidState,
} from '../../../utils/breadcrumbs';

// Apollo
import { useQuery } from '@apollo/client';
import { GET_SURVEILLANCE_ROUTE_BY_ID } from '../../../gql/queries/surveillanceRoute';
import { FETCH_TENANTS } from '../../../gql/queries/admin';

// Constants
import { formAccidentNaming } from '../../forms/AddBasicInfoAccidentForm/constants';
import { formNaming as formCommunicationNaming } from '../../forms/NewCommunicationForm/constants';
import { formActionNaming } from '../../forms/ActionForms/constants';
import { contextBelongsToPath } from '../../../utils/validationActionContext';
import { navigationContextBelongsTo } from '../../../utils/accidentEditDraft';
import { communicationContextBelongsTo } from '../../../utils/communicationEditDraft';

export const BreadCrumbs = ({ data: rawData = [] }: BreadCrumbsProps): JSX.Element => {
  // Clone data to prevent in-place mutations (splice, pop, content assignment)
  // from persisting across re-renders. WorkOrdersLayout doesn't re-render on
  // Redux changes, so BreadCrumbs can receive the same mutated array reference.
  let data = rawData.map(item => ({ ...item }));

  const { t } = useTranslation(['pages']);
  const location = useLocation();
  const dispatch = useDispatch();
  const roidSelected = useSelector(getRoidOTSelectedGV, isEqual);

  // ── ¿El ROID de Redux sigue siendo el de esta pantalla? ─────────────────
  //
  // Lo decide una máquina de estados pura (`utils/breadcrumbs.ts`), que es lo
  // que permite probar RECORRIDOS enteros: los fallos que ha tenido esta
  // lógica no aparecían en un salto, sino en el tercer o cuarto paso.
  //
  // Se avanza DURANTE el render, no en un efecto: la miga se pinta en el mismo
  // render en el que cambia la ruta, y un efecto llegaría un pintado tarde —
  // justo el instante que hay que tapar. Avanzar con las mismas entradas
  // devuelve el mismo estado, así que repintar es inofensivo.
  const crumbRoidStateRef = useRef(
    initialCrumbRoidState(location.pathname, roidSelected.id)
  );
  crumbRoidStateRef.current = nextCrumbRoidState(
    crumbRoidStateRef.current,
    location.pathname,
    roidSelected.id
  );

  // Read navigation context from session storage for Accidents
  // Una pantalla de validación de ACTUACIÓN nunca es de un accidente ni de una
  // comunicación: si su contexto sigue en la pestaña es de una visita anterior
  // y, como va primero en la cadena de `||` de abajo, pintaba su ROID en la
  // miga de la actuación (diagnóstico 2026-10-08 § 8).
  const isActionValidationPath = location.pathname.includes('/actions-validation/');

  // En EDITAR accidente, solo el contexto de ese accidente (`entityId`, que
  // escriben el lápiz y la tabla). Uno de otra visita se IGNORA —no se borra:
  // el detalle lo usa para su «Volver»—; antes la miga de la edición de A
  // pintaba la OT y el ROID de B (R5, spec 2026-11-02 wizard-draft-ownership).
  const accidentContext = useMemo(() => {
    if (isActionValidationPath) return null;
    const raw = sessionStorage.getItem(formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(decompressSessionStorage(raw));
      const segments = location.pathname.split('/');
      const editIndex = segments.indexOf('edit-accident');
      if (
        editIndex !== -1 &&
        !navigationContextBelongsTo(parsed, segments[editIndex + 1])
      ) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, [location.pathname]);

  // Read navigation context from session storage for Communications
  const communicationContext = useMemo(() => {
    if (isActionValidationPath) return null;
    const raw = sessionStorage.getItem(formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(decompressSessionStorage(raw));
      // En EDITAR comunicación, solo el contexto de esa comunicación
      // (`entityId`); el de otra visita se IGNORA sin borrarlo (el detalle lo
      // lee para su «Volver»). S2, auditoría 2026-11-02.
      const segments = location.pathname.split('/');
      const editIndex = segments.indexOf('edit-communication');
      if (
        editIndex !== -1 &&
        !communicationContextBelongsTo(parsed, segments[editIndex + 1])
      ) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, [location.pathname]);

  // Read navigation context from session storage for Actions (validation flow)
  // Solo si es de la actuación de la URL. El contexto lo escriben solo algunas
  // puertas (el ojo de la tarjeta, la tabla de Validación…) y se quedaba en la
  // pestaña al entrar después por otra (enlace de Tramos, «Resuelto por»,
  // atrás): la miga enseñaba el ROID de la actuación ANTERIOR, incluso en las
  // Órdenes asociadas de otro recorrido. Misma regla que la miga de Redux
  // (`utils/breadcrumbs.ts`): lo que no está en la URL no es de esta pantalla.
  const actionContext = useMemo(() => {
    const raw = sessionStorage.getItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(decompressSessionStorage(raw));
      return contextBelongsToPath(parsed, location.pathname) ? parsed : null;
    } catch {
      return null;
    }
  }, [location.pathname]);

  // V4 iter7+ (2026-05-07) — Issue D fix: cuando navegamos en
  // `/surveillance-route/:routeId/new-action/...`, el segmento intermedio del
  // breadcrumb (data[2]) muestra el Mongo ObjectId del routeId crudo en lugar
  // del ROID canónico. Causa: el wizard new-action dispatcha
  // `roidOTSelected(OrderType.Action, ...)` sobrescribiendo el del SR padre.
  // Solución: query del SR via el routeId del URL (cache-first; si el user
  // pasó por el detail antes ya estará cacheado, si no se hace fetch).
  const surveillanceRouteIdMatch = location.pathname.match(
    /^\/surveillance-route\/([0-9a-fA-F]{24})\b/,
  );
  const surveillanceRouteId = surveillanceRouteIdMatch?.[1] ?? null;
  const { data: srData } = useQuery(GET_SURVEILLANCE_ROUTE_BY_ID, {
    variables: { id: surveillanceRouteId ?? '' },
    skip: !surveillanceRouteId,
    fetchPolicy: 'cache-first',
  });
  const surveillanceParentRoid = useMemo(() => {
    const sr = srData?.surveillanceRoute;
    if (!sr) return null;
    return buildRoidFallback(sr) || null;
  }, [srData]);

  // Read navigation context from session storage for New Actions (creation flow)
  const newActionContext = useMemo(() => {
    const raw = sessionStorage.getItem(formActionNaming.NEW_ACTION_DATA);
    if (!raw) return null;
    try {
      return JSON.parse(decompressSessionStorage(raw));
    } catch {
      return null;
    }
  }, [location.pathname]);

  // ── El nombre del tenant en `/admin/tenants/:tenantId` ───────────────────
  //
  // Sale del catálogo por Apollo, igual que el ROID del recorrido de aquí
  // arriba, y NO de `sessionStorage`.
  //
  // 🔴 Lo de antes era `sessionStorage`, escrito por el loader del propio
  // detalle, y por eso el nombre solo aparecía al recargar. Con los loaders
  // diferidos la miga se pinta ANTES de que ese loader termine, así que en la
  // primera visita leía una clave que aún no existía —el listado acababa de
  // borrarla al montar— y se quedaba con el hueco de carga para siempre: el
  // almacén de sesión no avisa a nadie cuando cambia, y la única dependencia
  // del memo era la ruta, que ya no vuelve a cambiar. Al recargar sí salía,
  // porque para entonces la clave la había dejado escrita la visita anterior.
  //
  // El catálogo lo pide también el loader del detalle con `cache-first`, así
  // que esto no añade una petición: comparten entrada de caché.
  const adminTenantId = adminTenantIdFromPath(location.pathname);
  const { data: tenantsData } = useQuery(FETCH_TENANTS, {
    skip: !adminTenantId,
    fetchPolicy: 'cache-first',
  });
  const adminTenantName = useMemo(() => {
    if (!adminTenantId) return null;
    const tenant = tenantsData?.tenants?.find(
      (t: { id: string }) => t.id === adminTenantId,
    );
    return tenant?.name ?? null;
  }, [adminTenantId, tenantsData]);

  if (data[data.length - 1].path === '') {
    data.pop();
  }

  if (data[data.length - 1].path === 'new-campaign') {
    data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
  }

  // Check if this is actions-validation summary from ShowOT BEFORE the splice
  // URL: /work-orders/actions-validation/summary/:id
  // This needs special handling to show parent ROID instead of being spliced
  const isActionsValidationSummaryFromShowOT =
    data.length === 5 &&
    data[1]?.path === 'work-orders' &&
    data[2]?.path === 'actions-validation' &&
    data[3]?.path === 'summary' &&
    actionContext?.origin === 'workorder' &&
    actionContext?.parentRoid &&
    actionContext?.entityRoid;

  // Also skip splice for campaign actions-validation summary
  // URL: /work-orders/campaign/:id/actions-validation/summary/:actionId
  const isActionsValidationSummaryFromCampaign =
    data.length === 7 &&
    data[2]?.path === 'campaign' &&
    data[4]?.path === 'actions-validation' &&
    data[5]?.path === 'summary';

  // Igual para el recorrido de vigilancia (2026-08-15), que desde hoy tiene su
  // propio árbol de validación.
  // URL: /surveillance-route/:routeId/actions-validation/summary/:actionId
  const isActionsValidationSummaryFromSurveillanceRoute =
    data.length === 6 &&
    data[1]?.path === 'surveillance-route' &&
    data[3]?.path === 'actions-validation' &&
    data[4]?.path === 'summary';

  // Skip the summary splice for actions-validation from ShowOT, Campaign or
  // Surveillance Route so we can handle it properly in the switch case
  if (
    (data[data.length - 2].path === 'summary' ||
      data[data.length - 2].path === 'new-communication-duplicate' ||
      data[data.length - 2].path === 'new-order-duplicate') &&
    !isActionsValidationSummaryFromShowOT &&
    !isActionsValidationSummaryFromCampaign &&
    !isActionsValidationSummaryFromSurveillanceRoute
  ) {
    data = [
      ...data,
      (data[3].pathAbsolute = data[2].pathAbsolute + data[4].pathAbsolute),
    ] as RouteUIElement[];
    data.splice(data.length - 2, 2);
  }

  // Un ROID caducado no se pinta: sería un ROID que MIENTE sobre lo que hay
  // delante. Sale la marca de posición hasta que llegue el de esta pantalla.
  const isStaleRoid = crumbRoidStateRef.current.isExpired;

  // El mismo hueco que pone `createRoutes` mientras no hay ROID.
  const pendingContent = <PendingCrumb />;

  const breadCrumbContent = isStaleRoid ? (
    pendingContent
  ) : (
    <Typography size="S" colorText="greyDark">
      {formatRoidWithDots(roidSelected.roid)}
    </Typography>
  );

  const editAccidentContent = (
    <Typography size="S" colorText="greyDark">
      {t('workOrders.editAccident')}
    </Typography>
  );

  const editCommunicationContent = (
    <Typography size="S" colorText="greyDark">
      {t('workOrders.editCommunication')}
    </Typography>
  );

  const communicationSummaryContent = (
    <Typography size="S" colorText="greyDark">
      {t('navigation:routes.summary')}
    </Typography>
  );

  const campaignContent = (
    <Typography size="S" colorText="greyDark">
      {t('campaigns.title')}
    </Typography>
  );

  // Content for parent ROID from session context (Accidents/Communications/Actions detail view)
  const parentRoidContent = (accidentContext?.parentRoid || communicationContext?.parentRoid || actionContext?.parentRoid) ? (
    <Typography size="S" colorText="greyDark">
      {formatRoidWithDots(accidentContext?.parentRoid || communicationContext?.parentRoid || actionContext?.parentRoid)}
    </Typography>
  ) : null;

  // Content for entity ROID from session context (Accidents/Communications/Actions detail view)
  const entityRoidContent = (accidentContext?.entityRoid || communicationContext?.entityRoid || actionContext?.entityRoid) ? (
    <Typography size="S" colorText="greyDark">
      {formatRoidWithDots(accidentContext?.entityRoid || communicationContext?.entityRoid || actionContext?.entityRoid)}
    </Typography>
  ) : null;

  // V4 iter7+ (2026-05-07) — Issue D fix general (aplica antes del switch
  // por OrderType para cubrir casos donde roidSelected no está dispatched):
  // si el path es `/surveillance-route/<routeId>/...`, reemplazar el segmento
  // del ObjectId crudo por el ROID canónico del SR padre (leído via Apollo
  // useQuery con fetchPolicy cache-first).
  if (
    surveillanceParentRoid &&
    data.length >= 3 &&
    data[1]?.path === 'surveillance-route' &&
    data[2]?.path && /^[0-9a-fA-F]{24}$/.test(data[2].path)
  ) {
    data[2].content = (
      <Typography size="S" colorText="greyDark">
        {formatRoidWithDots(surveillanceParentRoid)}
      </Typography>
    );
  }

  // Ver/validar una actuación DENTRO del recorrido (2026-08-15).
  //
  // URL: /surveillance-route/:routeId/actions-validation[/summary]/:actionId[/pestaña]
  // Queremos: Recorregut de vigilància › [Ru.xxx] › [At.xxx] › [Pestaña]
  //
  // Va aquí y NO dentro del `switch (roidSelected.type)` a propósito, por el
  // mismo motivo que el bloque de arriba: `roidSelected` es estado de Redux y
  // no todos los puntos de entrada lo despachan —la tabla de tramos navega a
  // pelo—, así que dentro del switch estas migas saldrían bien o mal según por
  // dónde hubieras entrado. El andamio (`actions-validation`, `summary`) se
  // quita: bajo el recorrido esa ruta no tiene índice al que enlazar.
  const surveillanceActionValidation =
    data[1]?.path === 'surveillance-route' &&
    data[3]?.path === 'actions-validation';
  const isSurveillanceActionValidationSummary =
    surveillanceActionValidation && data.length === 6 && data[4]?.path === 'summary';
  const isSurveillanceActionValidationEntity =
    surveillanceActionValidation &&
    !isSurveillanceActionValidationSummary &&
    (data.length === 5 || data.length === 6);

  // El ROID de la ACTUACIÓN sale del contexto de sesión y, si no lo hay, del
  // Redux — pero SOLO si Redux está hablando de una actuación.
  //
  // 🔴 Este guard NO lo cubre la caducidad de `nextCrumbRoidState`, y parece
  // que sí. Esa máquina pregunta «¿el ROID de Redux es de ESTA pantalla?» y lo
  // decide viendo si su id sale en la URL. Aquí salen los DOS: la ruta es
  // `/surveillance-route/<recorrido>/actions-validation/summary/<actuación>`.
  // Así que entrando desde la tabla de tramos —que navega sin despachar nada—
  // Redux conserva el ROID del RECORRIDO, su id SÍ está en la URL, la máquina
  // lo da por vigente y esta miga pintaría el `Su.xxx` del recorrido haciéndolo
  // pasar por la actuación. Lo que distingue este caso no es de quién es el
  // ROID, sino de QUÉ habla; de ahí el tipo.
  //
  // Sin ROID fiable no se toca la miga: se queda la marca de posición que ya
  // puso `createRoutes` para el segmento de id (ver `utils/breadcrumbs.ts`), y
  // se resuelve en cuanto el resumen monta y despacha el suyo. Con los loaders
  // diferidos esa ventana existe SIEMPRE, no es un caso raro.
  const surveillanceActionCrumb =
    entityRoidContent ||
    (roidSelected.type === 'actions-validation' ? breadCrumbContent : null);

  if (isSurveillanceActionValidationSummary) {
    if (surveillanceActionCrumb) data[5].content = surveillanceActionCrumb;
    data[5].pathAbsolute = location.pathname;
    // Fuera 'actions-validation' y 'summary' — quedan Ru.xxx y At.xxx.
    data.splice(3, 2);
  } else if (isSurveillanceActionValidationEntity) {
    // El id de la actuación pasa a ROID y enlaza a SU resumen, que es la
    // pantalla de la que cuelgan las pestañas.
    if (surveillanceActionCrumb) data[4].content = surveillanceActionCrumb;
    data[4].pathAbsolute = `/surveillance-route/${data[2].path}/actions-validation/summary/${data[4].path}`;
    if (data[5]) data[5].pathAbsolute = location.pathname;
    data.splice(3, 1);
  }

  // Admin tenant detail: replace MongoDB ID with tenant name in breadcrumb
  // URL: /admin/tenants/:tenantId → breadcrumb segment 3 is the ID
  if (
    data.length >= 4 &&
    data[1]?.path === 'admin' &&
    data[2]?.path === 'tenants' &&
    adminTenantName
  ) {
    data[3].content = (
      <Typography size="S" colorText="greyDark">
        {adminTenantName}
      </Typography>
    );
  }

  // Reporting leaf pages (`/reporting/time-management`,
  // `/reporting/historical-actions`, etc.) tienen su label i18n correcto
  // ya resuelto por `getLabelToRoutes`. El switch siguiente
  // (`roidSelected.type`) está diseñado para flujos donde el último
  // segmento es un MongoDB ID y hay que sustituirlo por el ROID; si
  // entramos a una pantalla de Informes con `roidSelected.roid` cacheado
  // de una visita previa a una Order, el switch pisa el label con el
  // ROID y aparece "Ot.xxx" en lugar de "Gestión Horaria". Saltamos el
  // switch entero en ese caso y usamos los labels de `getLabelToRoutes`.
  const reportingLeafs = new Set([
    'time-management',
    'historical-actions',
    'historical-accidents',
    'historical-generic-orders',
    'historical-campaigns',
    'historical-communications',
    'historical-budgets',
  ]);
  const isReportingLeaf =
    data.length === 3 &&
    data[1]?.path === 'reporting' &&
    reportingLeafs.has(data[2]?.path);

  // Las migas del recorrido ya quedaron montadas arriba, y el switch —que
  // razona por longitudes— volvería a reescribir `pathAbsolute` sobre el array
  // YA recortado, rompiendo el enlace al recorrido. Mismo criterio que
  // `isReportingLeaf`: si otro bloque ya decidió, el switch no entra.
  if (!isReportingLeaf && !surveillanceActionValidation)
    switch (roidSelected.type) {
    case OrderType.Generic: {
      if (roidSelected.roid && data.length === 3) {
        data = [
          ...data,
          (data[2].content = breadCrumbContent),
        ] as RouteUIElement[];
        data.pop();
      }
      // Fixed for ROID in `/work-orders/:id/multimedia`
      else if (
        roidSelected.roid &&
        data.length === 4 &&
        data[3].path === 'multimedia'
      ) {
        data[2].content = breadCrumbContent;
        data[3].content = (
          <Typography size="S" colorText="greyDark">
            {t('navigation:routes.multimedia')}
          </Typography>
        );
      }
      break;
    }
    case OrderType.Communication: {
      // Case 0: Viewing communication detail from work order (/work-orders/communications/detail/:id)
      // When navigating from ShowOT, use session context to show parent ROID and entity ROID
      if (
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'communications' &&
        data[3].path === 'detail' &&
        communicationContext?.parentRoid &&
        communicationContext?.entityRoid
      ) {
        // Build path structure: work-orders > [Parent ROID] > communications > detail > [Entity ROID]
        // Replace the 'communications' segment with parent ROID link back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${communicationContext.parentOrderId}`;
        // 'detail' links to communications list
        data[3].pathAbsolute = '/work-orders/communications';
        // Replace MongoDB ID with entity ROID
        data[4].content = entityRoidContent;
        data[4].pathAbsolute = location.pathname;
      }
      // Case 1: Creating communication from work order (/work-orders/:id/new-communication)
      else if (
        roidSelected.roid &&
        data.length === 4 &&
        data[1].path === 'work-orders' &&
        data[3].path === 'new-communication'
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[2].content = breadCrumbContent;
      }
      // Case 2: Creating communication summary from work order (/work-orders/:id/new-communication/summary)
      else if (
        roidSelected.roid &&
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[3].path === 'new-communication'
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[2].content = breadCrumbContent;
      }
      // Case 2b: Creating communication from campaign (/work-orders/campaign/:id/new-communication)
      else if (
        roidSelected.roid &&
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'campaign' &&
        data[4].path === 'new-communication'
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[3].content = breadCrumbContent; // Replace campaign ID with ROID
      }
      // Case 2c: Creating communication summary from campaign (/work-orders/campaign/:id/new-communication/summary)
      else if (
        roidSelected.roid &&
        data.length === 6 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'campaign' &&
        data[4].path === 'new-communication'
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[5].pathAbsolute = data[4].pathAbsolute + '/' + data[5].path;
        data[3].content = breadCrumbContent; // Replace campaign ID with ROID
      }
      // Case Campaign: Communication detail from campaign (length 7)
      // Path: /work-orders/campaign/:campaignId/communications/detail/:communicationId
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Resumen > [Co.xxx]
      else if (
        data.length === 7 &&
        data[2].path === 'campaign' &&
        data[4].path === 'communications' &&
        data[5].path === 'detail'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        // "detail" becomes "Resumen"
        data[5].content = communicationSummaryContent;
        data[5].pathAbsolute = location.pathname;
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[6].content = entityRoidContent || breadCrumbContent;
        data[6].pathAbsolute = location.pathname;
        // Remove only "communications" (keep "detail" as "Resumen")
        data.splice(4, 1);
      }
      // Case 3: Viewing communication from general view (/work-orders/communications/:id)
      else if (roidSelected.roid && data.length === 4) {
        data = [
          ...data,
          (data[3].content = breadCrumbContent),
          (data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path),
        ] as RouteUIElement[];
        data.splice(data.length - 2, 2);
      }
      break;
    }
    case OrderType.Multimedia: {
      if (roidSelected.roid && data.length === 4) {
        data = [
          ...data,
          (data[3].content = breadCrumbContent),
          (data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path),
        ] as RouteUIElement[];
        data.splice(data.length - 2, 2);
      }
      // RV-anidada (2026-07-08): `/surveillance-route/:routeId/multimedia/:photoId`
      // (length 5). El segmento del routeId (data[2]) ya lo reemplaza el bloque
      // general SR de arriba por el ROID del RV; data[3] muestra el label
      // "Multimedia". Aquí solo falta sustituir el ObjectId de la foto (data[4])
      // por el ROID de la actuación, para no dejar el hash Mongo crudo.
      else if (
        roidSelected.roid &&
        data.length === 5 &&
        data[1]?.path === 'surveillance-route' &&
        data[3]?.path === 'multimedia'
      ) {
        data[4].content = breadCrumbContent;
      }
      break;
    }
    case OrderType.Campaign: {
      // Case 1: Viewing campaign detail (/work-orders/campaign/:id)
      if (roidSelected.roid && data.length === 4) {
        data[3].content = breadCrumbContent;
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
      }
      // Case 2: Creating communication from campaign (/work-orders/campaign/:id/new-communication)
      else if (
        roidSelected.roid &&
        data.length === 5 &&
        data[2].path === 'campaign' &&
        data[4].path === 'new-communication'
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[3].content = breadCrumbContent; // Replace campaign ID with ROID
      }
      // Case 2b: Viewing multimedia for campaign (/work-orders/campaign/:id/multimedia)
      else if (
        roidSelected.roid &&
        data.length === 5 &&
        data[2].path === 'campaign' &&
        data[4].path === 'multimedia'
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[3].content = breadCrumbContent; // Replace campaign ID with ROID
        data[4].content = (
          <Typography size="S" colorText="greyDark">
            {t('navigation:routes.multimedia')}
          </Typography>
        );
      }
      // Case 3: Creating communication summary from campaign (/work-orders/campaign/:id/new-communication/summary)
      else if (
        roidSelected.roid &&
        data.length === 6 &&
        data[2].path === 'campaign' &&
        data[4].path === 'new-communication'
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[5].pathAbsolute = data[4].pathAbsolute + '/' + data[5].path;
        data[3].content = breadCrumbContent; // Replace campaign ID with ROID
      }
      break;
    }
    case 'edit-campaign': {
      if (roidSelected.roid && data.length === 5) {
        data[4].content = breadCrumbContent;
        data[3].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[4].pathAbsolute = data[3].pathAbsolute;
      }
      if (roidSelected.roid && data.length === 6) {
        data[4].content = breadCrumbContent;
        data[3].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[4].pathAbsolute = data[3].pathAbsolute;
        data[5].pathAbsolute = data[4].pathAbsolute + '/' + data[5].path;
      }
      break;
    }

    case 'edit-communication': {
      // Case 0: Editing communication from work order (ShowOT) - WITHOUT subroute
      // Path: /work-orders/communications/edit-communication/:id (length 5)
      // Breadcrumb: work-orders > [Parent ROID] > Editar comunicación > [Entity ROID]
      const isEditCommunicationFromShowOT =
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'communications' &&
        data[3].path === 'edit-communication' &&
        communicationContext?.parentRoid &&
        communicationContext?.entityRoid;

      // Case 0b: Editing communication from work order (ShowOT) - WITH subroute
      // Path: /work-orders/communications/edit-communication/:id/summary (length 6)
      // Breadcrumb: work-orders > [Parent ROID] > Editar comunicación > [Entity ROID] > [Subroute]
      const isEditCommunicationWithSubrouteFromShowOT =
        data.length === 6 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'communications' &&
        data[3].path === 'edit-communication' &&
        communicationContext?.parentRoid &&
        communicationContext?.entityRoid;

      if (isEditCommunicationFromShowOT) {
        // Replace 'communications' with parent ROID linking back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${communicationContext.parentOrderId}`;
        // 'edit-communication' becomes "Editar comunicación" linking to communications list
        data[3].content = editCommunicationContent;
        data[3].pathAbsolute = '/work-orders/communications';
        // Replace MongoDB ID with entity ROID
        data[4].content = entityRoidContent;
        data[4].pathAbsolute = location.pathname;
      }
      // Case 0b: Edit communication WITH subroute from ShowOT (uses communicationContext)
      else if (isEditCommunicationWithSubrouteFromShowOT) {
        // Replace 'communications' with parent ROID linking back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${communicationContext.parentOrderId}`;
        // 'edit-communication' becomes "Editar comunicación"
        data[3].content = editCommunicationContent;
        data[3].pathAbsolute = '/work-orders/communications';
        // MongoDB ID becomes entity ROID, links to edit-communication base path
        data[4].content = entityRoidContent;
        data[4].pathAbsolute = `/work-orders/communications/edit-communication/${communicationContext.id || data[4].path}`;
        // Subroute keeps its label, links to current location
        data[5].pathAbsolute = location.pathname;
      }
      // Case Campaign: Edit communication from campaign WITHOUT subroute (length 7)
      // Path: /work-orders/campaign/:campaignId/communications/edit-communication/:id
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Editar Comunicación > [Co.xxx]
      else if (
        data.length === 7 &&
        data[2].path === 'campaign' &&
        data[4].path === 'communications' &&
        data[5].path === 'edit-communication'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[5].content = editCommunicationContent;
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[6].content = entityRoidContent || breadCrumbContent;
        data[6].pathAbsolute = location.pathname;
        const editBasePath = `/work-orders/campaign/${data[3].path}/communications/edit-communication/${data[6].path}`;
        data[5].pathAbsolute = editBasePath;
        // Remove "communications" (redundant)
        data.splice(4, 1);
      }
      // Case Campaign: Edit communication from campaign WITH subroute (length 8)
      // Path: /work-orders/campaign/:id/communications/edit-communication/:id/summary
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Editar Comunicación > [Co.xxx] > Resum
      else if (
        data.length === 8 &&
        data[2].path === 'campaign' &&
        data[4].path === 'communications' &&
        data[5].path === 'edit-communication'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[5].content = editCommunicationContent;
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[6].content = entityRoidContent || breadCrumbContent;
        const editBasePath = `/work-orders/campaign/${data[3].path}/communications/edit-communication/${data[6].path}`;
        data[5].pathAbsolute = editBasePath;
        data[6].pathAbsolute = editBasePath;
        data[7].pathAbsolute = location.pathname;
        // Remove "communications" (redundant)
        data.splice(4, 1);
      }
      // Case Campaign: Edit communication from campaign WITHOUT 'communications' segment (length 6)
      // Path: /work-orders/campaign/:campaignId/edit-communication/:commId
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Editar Comunicación > [Co.xxx]
      else if (
        data.length === 6 &&
        data[2].path === 'campaign' &&
        data[4].path === 'edit-communication'
      ) {
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[4].content = editCommunicationContent;
        data[5].content = entityRoidContent || breadCrumbContent;
        data[5].pathAbsolute = location.pathname;
        data[4].pathAbsolute = `/work-orders/campaign/${data[3].path}/edit-communication/${data[5].path}`;
      }
      // Case Campaign: Edit communication from campaign WITHOUT 'communications' WITH subroute (length 7)
      // Path: /work-orders/campaign/:campaignId/edit-communication/:commId/summary
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Editar Comunicación > [Co.xxx] > Resum
      else if (
        data.length === 7 &&
        data[2].path === 'campaign' &&
        data[4].path === 'edit-communication'
      ) {
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[4].content = editCommunicationContent;
        data[5].content = entityRoidContent || breadCrumbContent;
        const editBasePath = `/work-orders/campaign/${data[3].path}/edit-communication/${data[5].path}`;
        data[4].pathAbsolute = editBasePath;
        data[5].pathAbsolute = editBasePath;
        data[6].pathAbsolute = location.pathname;
      }
      // Case 1: Standard edit communication (length 5, not from ShowOT)
      else if (roidSelected.roid && data.length === 5) {
        data[3].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[4].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[3].content = editCommunicationContent;
        data[4].content = breadCrumbContent;
      }
      // Case 2: Edit communication with extra segment (length 6, not from ShowOT)
      else if (roidSelected.roid && data.length === 6) {
        data[3].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[4].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[5].pathAbsolute = data[4].pathAbsolute + '/' + data[5].path;
        data[3].content = editCommunicationContent;
        data[4].content = breadCrumbContent;
      }
      break;
    }
    case OrderType.Accident: {
      // Case 0: Viewing accident detail from work order (/work-orders/accidents/detail/:id)
      // When navigating from ShowOT, use session context to show parent ROID and entity ROID
      const isAccidentDetailFromShowOT =
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'accidents' &&
        data[3].path === 'detail' &&
        accidentContext?.parentRoid &&
        accidentContext?.entityRoid;

      if (isAccidentDetailFromShowOT) {
        // Build path structure: work-orders > [Parent ROID] > Detalle > [Entity ROID]
        // Replace the 'accidents' segment with parent ROID link back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${accidentContext.parentOrderId}`;
        // 'detail' links to accidents list
        data[3].pathAbsolute = '/work-orders/accidents';
        // Replace MongoDB ID with entity ROID
        data[4].content = entityRoidContent;
        data[4].pathAbsolute = location.pathname;
      }
      // Case 1: Standard accident view with roidSelected (length 4)
      else if (
        roidSelected.roid &&
        data.length === 4 &&
        data.every(
          (item) => item.path !== 'campaign' && item.path !== 'edit-accident'
        )
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        if (data[1].path === 'work-orders' && data[2].path === 'accidents') {
          data[3].content = breadCrumbContent;
        } else {
          data[2].content = breadCrumbContent;
        }
      }
      // Case 2: Standard accident view with roidSelected (length 5, NOT from ShowOT detail)
      else if (
        roidSelected.roid &&
        data.length === 5 &&
        data.every(
          (item) => item.path !== 'campaign' && item.path !== 'edit-accident'
        )
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[2].content = breadCrumbContent;
      }
      // Case 3: Campaign accidents (length 5)
      else if (
        roidSelected.roid &&
        data.length === 5 &&
        data.some((item) => item.path === 'campaign')
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[3].content = breadCrumbContent;
      }
      // Case 4: Campaign accidents (length 6)
      else if (
        roidSelected.roid &&
        data.length === 6 &&
        data.some((item) => item.path === 'campaign')
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[5].pathAbsolute = data[4].pathAbsolute + '/' + data[5].path;
        data[3].content = breadCrumbContent;
      }
      // Case 5: Campaign accident detail (/work-orders/campaign/:id/accidents/detail/:accidentId)
      // data: [Home, work-orders, campaign, :campaignId, accidents, detail, :accidentId] (length 7)
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Accidentes > [Ac.xxx]
      else if (
        data.length === 7 &&
        data[2].path === 'campaign' &&
        data[4].path === 'accidents' &&
        data[5].path === 'detail'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[4].pathAbsolute = data[3].pathAbsolute + '/accidents';
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[6].content = entityRoidContent || breadCrumbContent;
        data[6].pathAbsolute = location.pathname;
        // Remove "detail" (redundant)
        data.splice(5, 1);
      }
      break;
    }

    case 'edit-accident': {
      // Case 0: Editing accident from work order (ShowOT) - WITHOUT subroute
      // Path: /work-orders/accidents/edit-accident/:id (length 5)
      // Breadcrumb: work-orders > [Parent ROID] > Editar accidente > [Entity ROID]
      const isEditAccidentFromShowOT =
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'accidents' &&
        data[3].path === 'edit-accident' &&
        accidentContext?.parentRoid &&
        accidentContext?.entityRoid;

      // Case 0b: Editing accident from work order (ShowOT) - WITH subroute
      // Path: /work-orders/accidents/edit-accident/:id/basic-info (length 6)
      // Breadcrumb: work-orders > [Parent ROID] > Editar accidente > [Entity ROID] > [Subroute]
      const isEditAccidentWithSubrouteFromShowOT =
        data.length === 6 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'accidents' &&
        data[3].path === 'edit-accident' &&
        accidentContext?.parentRoid &&
        accidentContext?.entityRoid;

      if (isEditAccidentFromShowOT) {
        // Replace 'accidents' with parent ROID linking back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${accidentContext.parentOrderId}`;
        // 'edit-accident' becomes "Editar accidente" linking to accidents list
        data[3].content = editAccidentContent;
        data[3].pathAbsolute = '/work-orders/accidents';
        // Replace MongoDB ID with entity ROID
        data[4].content = entityRoidContent;
        data[4].pathAbsolute = location.pathname;
      }
      // Case 0b: Edit accident WITH subroute from ShowOT (uses accidentContext)
      else if (isEditAccidentWithSubrouteFromShowOT) {
        // Replace 'accidents' with parent ROID linking back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${accidentContext.parentOrderId}`;
        // 'edit-accident' becomes "Editar accidente"
        data[3].content = editAccidentContent;
        data[3].pathAbsolute = '/work-orders/accidents';
        // MongoDB ID becomes entity ROID, links to edit-accident base path
        data[4].content = entityRoidContent;
        data[4].pathAbsolute = `/work-orders/accidents/edit-accident/${accidentContext.id || data[4].path}`;
        // Subroute keeps its label, links to current location
        data[5].pathAbsolute = location.pathname;
      }
      // Case Campaign: Edit accident from campaign WITHOUT subroute (length 7)
      // Path: /work-orders/campaign/:campaignId/accidents/edit-accident/:accidentId
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Editar > [Ac.xxx]
      else if (
        data.length === 7 &&
        data[2].path === 'campaign' &&
        data[4].path === 'accidents' &&
        data[5].path === 'edit-accident'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[5].content = editAccidentContent;
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[6].content = entityRoidContent || breadCrumbContent;
        data[6].pathAbsolute = location.pathname;
        const editBasePath = `/work-orders/campaign/${data[3].path}/accidents/edit-accident`;
        data[5].pathAbsolute = editBasePath;
        // Remove "accidents" (redundant between campaign and edit-accident)
        data.splice(4, 1);
      }
      // Case Campaign: Edit accident from campaign WITH subroute (length 8)
      // Path: /work-orders/campaign/:id/accidents/edit-accident/:id/basic-info
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Editar > [Ac.xxx] > Info Básica
      else if (
        data.length === 8 &&
        data[2].path === 'campaign' &&
        data[4].path === 'accidents' &&
        data[5].path === 'edit-accident'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[5].content = editAccidentContent;
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[6].content = entityRoidContent || breadCrumbContent;
        const editBasePath = `/work-orders/campaign/${data[3].path}/accidents/edit-accident/${data[6].path}`;
        data[5].pathAbsolute = editBasePath;
        data[6].pathAbsolute = editBasePath;
        data[7].pathAbsolute = location.pathname;
        // Remove "accidents" (redundant)
        data.splice(4, 1);
      }
      // Case 1: Standard edit accident (length 5, not from ShowOT)
      else if (roidSelected.roid && data.length === 5) {
        data[3].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[4].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[3].content = editAccidentContent;
        data[4].content = breadCrumbContent;
      }
      // Case 2: Edit accident with extra segment (length 6, not from ShowOT)
      else if (roidSelected.roid && data.length === 6) {
        data[3].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[4].pathAbsolute =
          data[2].pathAbsolute + '/' + data[3].path + '/' + data[4].path;
        data[5].pathAbsolute = data[4].pathAbsolute + '/' + data[5].path;
        data[3].content = editAccidentContent;
        data[4].content = breadCrumbContent;
      }
      break;
    }

    case OrderType.Action: {
      // Detectar si estamos en el flujo de new-action (crear actuación desde orden)
      // En este flujo, data[2] es el orderId y NO debe ser reemplazado con el ROID de la actuación
      const isNewActionFlow = data.some((item) => item.path === 'new-action');

      // Content para el ROID de la orden desde newActionContext (usado en new-action)
      // newActionContext viene de NEW_ACTION_DATA y contiene orderParentRoid
      const orderRoidContent = newActionContext?.orderParentRoid ? (
        <Typography size="S" colorText="greyDark">
          {formatRoidWithDots(newActionContext.orderParentRoid)}
        </Typography>
      ) : null;

      if (
        roidSelected.roid &&
        data.length >= 4 &&
        data.every((item) => item.path !== 'campaign') &&
        !isNewActionFlow
      ) {
        // Caso estándar: NO es new-action, reemplazar con ROID de actuación
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[2].content = breadCrumbContent;
      }

      // Caso new-action: Usar ROID de la orden, no de la actuación
      if (
        roidSelected.roid &&
        data.length >= 4 &&
        data.every((item) => item.path !== 'campaign') &&
        isNewActionFlow
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        // Usar el ROID de la orden si está disponible en newActionContext
        if (orderRoidContent) {
          data[2].content = orderRoidContent;
        }
        // V4 iter7+ (2026-05-07): si estamos en SR mode (URL contiene
        // /surveillance-route/<id>/new-action/...) preferimos el ROID
        // canónico del SR padre leído del Apollo cache. Cubre el caso en que
        // newActionContext no tiene `orderParentRoid` o el wizard se abrió
        // por navegación directa.
        else if (surveillanceParentRoid) {
          data[2].content = (
            <Typography size="S" colorText="greyDark">
              {formatRoidWithDots(surveillanceParentRoid)}
            </Typography>
          );
        }
        // Si no hay nada, mantener el contenido original (ID de MongoDB)
      }

      if (
        roidSelected.roid &&
        data.length === 5 &&
        data.every((item) => item.path !== 'campaign') &&
        !isNewActionFlow
      ) {
        data[4].pathAbsolute =
          data[3].pathAbsolute + '/' + data[4].path + '/' + roidSelected.id;
      }

      if (
        roidSelected.roid &&
        data.length === 6 &&
        data.every((item) => item.path !== 'campaign')
      ) {
        data[4].pathAbsolute =
          data[3].pathAbsolute + '/details/' + data[4].path;
        data[5].pathAbsolute =
          data[3].pathAbsolute + '/details/' + data[5].path;
      }

      if (roidSelected.roid && data.length === 7) {
        data[2].content = campaignContent;
        // Use campaign ROID from newActionContext, not action ROID
        if (orderRoidContent) {
          data[3].content = orderRoidContent;
        }
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[5].pathAbsolute = data[4].pathAbsolute + '/' + data[5].path;
        data[6].pathAbsolute = data[5].pathAbsolute + '/' + data[6].path;
      }

      // For campaigns and actions - use orderRoidContent (campaign ROID) not breadCrumbContent (action ROID)
      if (
        roidSelected.roid &&
        data.length === 5 &&
        data.some((item) => item.path === 'campaign')
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        // Use campaign ROID from newActionContext, not action ROID
        if (orderRoidContent) {
          data[3].content = orderRoidContent;
        }
      }

      if (
        roidSelected.roid &&
        data.length === 6 &&
        data.some((item) => item.path === 'campaign')
      ) {
        data[2].content = campaignContent;
        // Use campaign ROID from newActionContext, not action ROID
        if (orderRoidContent) {
          data[3].content = orderRoidContent;
        }
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[5].pathAbsolute =
          data[4].pathAbsolute + '/' + data[5].path + '/' + roidSelected.id;
      }

      if (
        roidSelected.roid &&
        data.length === 7 &&
        data.some((item) => item.path === 'campaign')
      ) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
      }
      break;
    }
    case 'actions-validation': {
      // Lista de subrutas conocidas para validación de acciones
      const validationSubroutes = ['operators-and-vehicles', 'location', 'edition', 'budget-items', 'materials', 'machinery', 'animals'];

      // Case 0: Viewing action validation SUMMARY from work order (ShowOT)
      // Path: /work-orders/actions-validation/summary/:id (length 5)
      // When origin is 'workorder' and data[3].path === 'summary'
      const isActionValidationSummaryFromShowOT =
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'actions-validation' &&
        data[3].path === 'summary' &&
        actionContext?.origin === 'workorder' &&
        actionContext?.parentRoid &&
        actionContext?.entityRoid;

      // Case 1: Viewing action validation SUBROUTE from work order (ShowOT)
      // Path: /work-orders/actions-validation/:id/[subroute] (length 5)
      // When origin is 'workorder' and data[4].path is a known subroute
      const isActionValidationSubrouteFromShowOT =
        data.length === 5 &&
        data[1].path === 'work-orders' &&
        data[2].path === 'actions-validation' &&
        validationSubroutes.includes(data[4].path) &&
        actionContext?.origin === 'workorder' &&
        actionContext?.parentRoid &&
        actionContext?.entityRoid;

      if (isActionValidationSummaryFromShowOT) {
        // Build path structure: work-orders > [Parent ROID] > [Entity ROID]
        // Replace 'actions-validation' with parent ROID linking back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${actionContext.parentOrderId}`;
        // Replace 'summary' with entity ROID (skip showing "Resumen" label)
        data[3].content = entityRoidContent;
        data[3].pathAbsolute = location.pathname;
        // Remove the MongoDB ID item at position 4 (no longer needed)
        data.splice(4, 1);
      }
      else if (isActionValidationSubrouteFromShowOT) {
        // Build path structure: work-orders > [Parent ROID] > [Entity ROID] > [Subroute Label]
        // Replace 'actions-validation' with parent ROID linking back to work order
        data[2].content = parentRoidContent;
        data[2].pathAbsolute = `/work-orders/${actionContext.parentOrderId}`;
        // Replace MongoDB ID (data[3]) with entity ROID, link to summary
        data[3].content = entityRoidContent;
        data[3].pathAbsolute = `/work-orders/actions-validation/summary/${actionContext.id || data[3].path}`;
        // data[4] keeps its original label (Operarios, Ubicación, etc.)
        data[4].pathAbsolute = location.pathname;
      }
      // Case Campaign Summary: Action validation SUMMARY from campaign (length 7)
      // Path: /work-orders/campaign/:campaignId/actions-validation/summary/:actionId
      // data: [Home, work-orders, campaign, :campaignId, actions-validation, summary, :actionId]
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > [At.xxx]
      else if (
        data.length === 7 &&
        data[2].path === 'campaign' &&
        data[4].path === 'actions-validation' &&
        data[5].path === 'summary'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[6].content = entityRoidContent || breadCrumbContent;
        data[6].pathAbsolute = location.pathname;
        // Remove 'actions-validation' and 'summary' — keep Ca.xxx and At.xxx
        data.splice(4, 2);
      }
      // Case Campaign: Action validation from campaign (length 6)
      // Path: /work-orders/campaign/:campaignId/actions-validation/:actionId
      // data: [Home, work-orders, campaign, :campaignId, actions-validation, :actionId]
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Actuaciones > [Action ROID]
      else if (
        data.length === 6 &&
        data[2].path === 'campaign' &&
        data[4].path === 'actions-validation'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[4].pathAbsolute = data[3].pathAbsolute + '/actions-validation';
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[5].content = entityRoidContent || breadCrumbContent;
        data[5].pathAbsolute = location.pathname;
      }
      // Case Campaign: Action validation from campaign with subroute (length 7)
      // Path: /work-orders/campaign/:campaignId/actions-validation/:actionId/operators-and-vehicles
      // Desired: Órdenes de trabajo > Campañas > [Ca.xxx] > Actuaciones > [Action ROID] > Operaris
      else if (
        data.length === 7 &&
        data[2].path === 'campaign' &&
        data[4].path === 'actions-validation'
      ) {
        // Campaign ROID: use sessionStorage if available, else keep raw ID
        if (parentRoidContent) {
          data[3].content = parentRoidContent;
        }
        data[3].pathAbsolute = `/work-orders/campaign/${data[3].path}`;
        data[4].pathAbsolute = data[3].pathAbsolute + '/actions-validation';
        // Entity ROID: sessionStorage > roidSelected.roid > raw ID
        data[5].content = entityRoidContent || breadCrumbContent;
        data[5].pathAbsolute = `/work-orders/campaign/${data[3].path}/actions-validation/${data[5].path}`;
        data[6].pathAbsolute = location.pathname;
      }
      // Case 2: Standard validation view (length 4, from validation table)
      else if (roidSelected.roid && data.length === 4) {
        data[3].content = breadCrumbContent;
      }
      // Case 3: Standard validation view with summary/subroute (length 5, from validation table)
      else if (roidSelected.roid && data.length === 5) {
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
        data[4].pathAbsolute = data[3].pathAbsolute + '/' + data[4].path;
        data[3].content = breadCrumbContent;
      }
      break;
    }
    case OrderType.SurveillanceRoute: {
      if (roidSelected.roid && data.length === 3) {
        data[2].content = breadCrumbContent;
      } else if (roidSelected.roid && data.length === 4) {
        data[2].content = breadCrumbContent;
        data[3].pathAbsolute = data[2].pathAbsolute + '/' + data[3].path;
      }
      break;
    }
    default: {
      if (roidSelected.roid && data.length === 3) {
        data = [
          ...data,
          (data[2].content = breadCrumbContent),
        ] as RouteUIElement[];
        data.pop();
      }
    }
  }

  // Validar una actuación abierta desde la pestaña Actuaciones
  // (`returnTo: '/work-orders/actions'`): el eslabón de `actions-validation` se
  // llama y enlaza como esa pestaña, que es adonde se vuelve al guardar. Sin
  // esto enlazaba a la página suelta de Validación de actuaciones, que no tiene
  // pestañas (spec 2026-10-06-return-to-origin § 4 E3). Va DESPUÉS del switch y
  // sin mirar `roidSelected`, que no todas las entradas despachan. El índice de
  // la página de validación (longitud 3) no entra: allí sí estás en ella.
  if (
    data.length > 3 &&
    data[1]?.path === 'work-orders' &&
    data[2]?.path === 'actions-validation' &&
    actionContext?.returnTo === ACTIONS_TAB_PATH
  ) {
    data[2].content = (
      <Typography size="S" colorText="greyDark">
        {getRouteLabel('actions')}
      </Typography>
    );
    data[2].pathAbsolute = ACTIONS_TAB_PATH;
  }

  const handleBreadcrumbClick = () => {
    dispatch(indexTabFormReset(tabsType.WORK_ORDERS_GENERAL));
    dispatch(indexTabFormReset(tabsType.COMMUNICATION));
    dispatch(indexTabFormReset(tabsType.DUPLICATE_COMMUNICATION));
    dispatch(indexTabFormReset(tabsType.WORK_ORDERS_ACTION));
    dispatch(indexTabFormReset(tabsType.SURVEILLANCE_ROUTE));
    dispatch(indexTabFormReset(tabsType.TRANSFER_SUBORDER));
    dispatch(indexTabFormReset(tabsType.EDIT_COMMUNICATION));
  };

  return (
    // `data-testid`: el menú lateral también enlaza a estas rutas con el mismo
    // texto, y los tests necesitan mirar SOLO la miga.
    <Container data-testid="breadcrumbs">
      {data.map((breadcrumb, index) => {
        return (
          <Item key={index.toString()}>
            <Link to={breadcrumb.pathAbsolute} onClick={handleBreadcrumbClick}>
              {breadcrumb.content}
            </Link>
            {index !== data.length - 1 && <RightButton />}
          </Item>
        );
      })}
    </Container>
  );
};
